import React, { useState } from 'react';
import { X, Shield, Save, Eye, Edit, Plus } from '../components/Icons';
import { AVAILABLE_ROLES as INITIAL_ROLES, ROLE_DETAILED_PERMISSIONS as INITIAL_PERMISSIONS, PERMISSION_POINTS } from '../data/mockData';

// 所有菜单页面
const ALL_PAGES = [
  { id: 'dashboard', label: '工作台' },
  { id: 'approval', label: '审批管理' },
  { id: 'base', label: '基地管理' },
  { id: 'materials', label: '基地物料' },
  { id: 'demand', label: '采购需求' },
  { id: 'order', label: '订单管理' },
  { id: 'delivery', label: '送货单管理' },
  { id: 'arrival_notice', label: '预到货通知' },
  { id: 'stockin_manage', label: '入库单管理' },
  { id: 'settlement_manage', label: '结算单管理' },
  { id: 'invoice_manage', label: '发票单管理' },
  { id: 'payment', label: '付款单管理' },
  { id: 'users', label: '客商管理' },
  { id: 'sub_account', label: '子账号管理' },
  { id: 'role_permission', label: '岗位权限' },
  { id: 'operation_log', label: '税务查证' },
];

// 数据权限范围选项
const DATA_SCOPE_OPTIONS = [
  { value: 'all', label: '全部数据', description: '可查看和操作所有基地的数据' },
  { value: 'base', label: '所属基地', description: '仅可查看和操作所属基地的数据' },
  { value: 'self', label: '仅自己', description: '仅可查看和操作自己创建的数据' },
];

const RolePermissionView = () => {
  const [roles, setRoles] = useState(INITIAL_ROLES);
  const [rolePermissions, setRolePermissions] = useState(INITIAL_PERMISSIONS);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [localPermissions, setLocalPermissions] = useState(null);
  const [newRole, setNewRole] = useState({ id: '', label: '', description: '' });
  const [newRolePermissions, setNewRolePermissions] = useState({ pages: [], dataScope: 'base', permissions: {} });

  // 获取角色权限配置
  const getRolePermission = (roleId) => {
    return rolePermissions[roleId] || { pages: [], dataScope: 'base', permissions: {} };
  };

  // 打开编辑弹窗
  const handleEditRole = (role) => {
    const perm = getRolePermission(role.id);
    setEditingRole(role);
    setLocalPermissions(JSON.parse(JSON.stringify(perm)));
    setShowEditModal(true);
  };

  // 切换页面权限
  const togglePagePermission = (pageId) => {
    const pages = [...(localPermissions.pages || [])];
    const index = pages.indexOf(pageId);
    if (index > -1) {
      pages.splice(index, 1);
    } else {
      pages.push(pageId);
    }
    setLocalPermissions({ ...localPermissions, pages });
  };

  // 切换功能权限
  const toggleFunctionPermission = (module, permKey) => {
    const permissions = { ...(localPermissions.permissions || {}) };
    if (!permissions[module]) {
      permissions[module] = [];
    }
    const index = permissions[module].indexOf(permKey);
    if (index > -1) {
      permissions[module].splice(index, 1);
      if (permissions[module].length === 0) {
        delete permissions[module];
      }
    } else {
      permissions[module].push(permKey);
    }
    setLocalPermissions({ ...localPermissions, permissions });
  };

  // 保存权限配置
  const handleSavePermissions = () => {
    setRolePermissions(prev => ({
      ...prev,
      [editingRole.id]: localPermissions
    }));
    setShowEditModal(false);
    setEditingRole(null);
    setLocalPermissions(null);
    alert('权限配置已保存');
  };

  // 新建岗位
  const handleAddRole = () => {
    if (!newRole.id || !newRole.label) {
      alert('请填写岗位ID和岗位名称');
      return;
    }
    if (roles.find(r => r.id === newRole.id)) {
      alert('岗位ID已存在，请使用其他ID');
      return;
    }

    const roleToAdd = {
      id: newRole.id,
      label: newRole.label,
      description: newRole.description || newRole.label
    };

    setRoles(prev => [...prev, roleToAdd]);
    setRolePermissions(prev => ({
      ...prev,
      [newRole.id]: newRolePermissions
    }));

    setShowAddModal(false);
    setNewRole({ id: '', label: '', description: '' });
    setNewRolePermissions({ pages: [], dataScope: 'base', permissions: {} });
    alert(`岗位「${newRole.label}」创建成功`);
  };

  // 删除岗位
  const handleDeleteRole = (role) => {
    if (role.id === 'admin') {
      alert('超级管理员岗位不可删除');
      return;
    }
    if (window.confirm(`确定要删除岗位「${role.label}」吗？删除后不可恢复。`)) {
      setRoles(prev => prev.filter(r => r.id !== role.id));
      setRolePermissions(prev => {
        const newPerms = { ...prev };
        delete newPerms[role.id];
        return newPerms;
      });
      alert(`岗位「${role.label}」已删除`);
    }
  };

  // 切换新建岗位的页面权限
  const toggleNewRolePagePermission = (pageId) => {
    const pages = [...(newRolePermissions.pages || [])];
    const index = pages.indexOf(pageId);
    if (index > -1) {
      pages.splice(index, 1);
    } else {
      pages.push(pageId);
    }
    setNewRolePermissions({ ...newRolePermissions, pages });
  };

  // 切换新建岗位的功能权限
  const toggleNewRoleFunctionPermission = (module, permKey) => {
    const permissions = { ...(newRolePermissions.permissions || {}) };
    if (!permissions[module]) {
      permissions[module] = [];
    }
    const index = permissions[module].indexOf(permKey);
    if (index > -1) {
      permissions[module].splice(index, 1);
      if (permissions[module].length === 0) {
        delete permissions[module];
      }
    } else {
      permissions[module].push(permKey);
    }
    setNewRolePermissions({ ...newRolePermissions, permissions });
  };

  // 检查新建岗位功能权限是否启用
  const isNewRoleFunctionPermissionEnabled = (moduleId, permKey) => {
    return (newRolePermissions?.permissions?.[moduleId] || []).includes(permKey);
  };


  // 检查功能权限是否启用
  const isFunctionPermissionEnabled = (moduleId, permKey) => {
    return (localPermissions?.permissions?.[moduleId] || []).includes(permKey);
  };

  // 编辑权限弹窗
  const EditPermissionModal = () => {
    if (!editingRole || !localPermissions) return null;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] animate-in fade-in zoom-in duration-200">
        <div className="bg-white rounded-xl shadow-xl w-[900px] p-0 overflow-hidden flex flex-col max-h-[90vh]">
          <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-white sticky top-0 z-10">
            <div>
              <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <Shield size={20} className="text-blue-600" />
                编辑岗位权限
              </h3>
              <p className="text-sm text-slate-500 mt-1">
                岗位：<span className="font-medium text-slate-700">{editingRole.label}</span>
              </p>
            </div>
            <button onClick={() => setShowEditModal(false)}>
              <X size={20} className="text-slate-400 hover:text-slate-600" />
            </button>
          </div>

          <div className="p-6 space-y-6 overflow-y-auto">
            {/* 页面权限 */}
            <div>
              <h4 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
                <Eye size={16} className="text-green-600" />
                页面权限（菜单可见性）
              </h4>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <div className="grid grid-cols-4 gap-2">
                  {ALL_PAGES.map(page => {
                    const isEnabled = (localPermissions.pages || []).includes(page.id);
                    return (
                      <label
                        key={page.id}
                        className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                          isEnabled ? 'bg-blue-50 border border-blue-200' : 'bg-white border border-slate-200 hover:bg-slate-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isEnabled}
                          onChange={() => togglePagePermission(page.id)}
                          className="w-4 h-4 text-blue-600 rounded"
                        />
                        <span className={`text-sm ${isEnabled ? 'text-blue-700 font-medium' : 'text-slate-600'}`}>
                          {page.label}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* 数据权限 */}
            <div>
              <h4 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
                <Edit size={16} className="text-purple-600" />
                数据权限范围
              </h4>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <div className="grid grid-cols-3 gap-3">
                  {DATA_SCOPE_OPTIONS.map(option => (
                    <label
                      key={option.value}
                      className={`flex flex-col p-3 rounded-lg cursor-pointer transition-colors border ${
                        localPermissions.dataScope === option.value
                          ? 'bg-purple-50 border-purple-300'
                          : 'bg-white border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="dataScope"
                          value={option.value}
                          checked={localPermissions.dataScope === option.value}
                          onChange={() => setLocalPermissions({ ...localPermissions, dataScope: option.value })}
                          className="w-4 h-4 text-purple-600"
                        />
                        <span className={`text-sm font-medium ${
                          localPermissions.dataScope === option.value ? 'text-purple-700' : 'text-slate-700'
                        }`}>
                          {option.label}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1 ml-6">{option.description}</p>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* 功能权限 */}
            <div>
              <h4 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
                <Shield size={16} className="text-orange-600" />
                功能操作权限
              </h4>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <div className="space-y-4">
                  {Object.entries(PERMISSION_POINTS).map(([moduleId, perms]) => {
                    const modulePages = {
                      demand: '采购需求',
                      users: '客商管理',
                      order: '订单管理',
                      delivery: '送货单管理',
                      stockin: '入库单管理',
                      settlement: '结算管理',
                      invoice: '发票管理',
                      payment: '支付管理',
                      base: '基地管理',
                      materials: '物料管理',
                      employees: '员工管理',
                    };
                    return (
                      <div key={moduleId} className="bg-white p-3 rounded-lg border border-slate-200">
                        <h5 className="text-sm font-medium text-slate-700 mb-2">{modulePages[moduleId] || moduleId}</h5>
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(perms).map(([permKey, permLabel]) => {
                            const isEnabled = isFunctionPermissionEnabled(moduleId, permKey);
                            return (
                              <label
                                key={`${moduleId}-${permKey}`}
                                className={`flex items-center gap-1.5 px-2.5 py-1 rounded cursor-pointer transition-colors border text-xs ${
                                  isEnabled
                                    ? 'bg-orange-50 border-orange-200 text-orange-700'
                                    : 'bg-slate-50 border-slate-200 text-slate-500 hover:bg-slate-100'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isEnabled}
                                  onChange={() => toggleFunctionPermission(moduleId, permKey)}
                                  className="w-3 h-3 text-orange-600 rounded"
                                />
                                <span>{permLabel}</span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          <div className="p-4 border-t border-slate-100 bg-white flex justify-end gap-3">
            <button
              onClick={() => setShowEditModal(false)}
              className="px-4 py-2 text-slate-600 text-sm font-medium hover:bg-slate-50 rounded-lg"
            >
              取消
            </button>
            <button
              onClick={handleSavePermissions}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <Save size={16} /> 保存配置
            </button>
          </div>
        </div>
      </div>
    );
  };

  // 新建岗位弹窗
  const AddRoleModal = () => {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] animate-in fade-in zoom-in duration-200">
        <div className="bg-white rounded-xl shadow-xl w-[900px] p-0 overflow-hidden flex flex-col max-h-[90vh]">
          <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-white sticky top-0 z-10">
            <div>
              <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <Plus size={20} className="text-green-600" />
                新建岗位
              </h3>
              <p className="text-sm text-slate-500 mt-1">
                创建新岗位并配置权限
              </p>
            </div>
            <button onClick={() => setShowAddModal(false)}>
              <X size={20} className="text-slate-400 hover:text-slate-600" />
            </button>
          </div>

          <div className="p-6 space-y-6 overflow-y-auto">
            {/* 岗位基本信息 */}
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <h4 className="text-sm font-bold text-slate-700 mb-3">基本信息</h4>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    岗位ID <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className="w-full border border-slate-200 rounded px-3 py-2 text-sm"
                    placeholder="例如：sales_manager"
                    value={newRole.id}
                    onChange={e => setNewRole({ ...newRole, id: e.target.value.replace(/\s/g, '_').toLowerCase() })}
                  />
                  <p className="text-xs text-slate-400 mt-1">唯一标识，只能包含字母、数字和下划线</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    岗位名称 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className="w-full border border-slate-200 rounded px-3 py-2 text-sm"
                    placeholder="例如：销售经理"
                    value={newRole.label}
                    onChange={e => setNewRole({ ...newRole, label: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    岗位描述
                  </label>
                  <input
                    type="text"
                    className="w-full border border-slate-200 rounded px-3 py-2 text-sm"
                    placeholder="简要描述岗位职责"
                    value={newRole.description}
                    onChange={e => setNewRole({ ...newRole, description: e.target.value })}
                  />
                </div>
              </div>
            </div>

            {/* 页面权限 */}
            <div>
              <h4 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
                <Eye size={16} className="text-green-600" />
                页面权限（菜单可见性）
              </h4>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <div className="grid grid-cols-4 gap-2">
                  {ALL_PAGES.map(page => {
                    const isEnabled = (newRolePermissions.pages || []).includes(page.id);
                    return (
                      <label
                        key={page.id}
                        className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                          isEnabled ? 'bg-blue-50 border border-blue-200' : 'bg-white border border-slate-200 hover:bg-slate-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isEnabled}
                          onChange={() => toggleNewRolePagePermission(page.id)}
                          className="w-4 h-4 text-blue-600 rounded"
                        />
                        <span className={`text-sm ${isEnabled ? 'text-blue-700 font-medium' : 'text-slate-600'}`}>
                          {page.label}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* 数据权限 */}
            <div>
              <h4 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
                <Edit size={16} className="text-purple-600" />
                数据权限范围
              </h4>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <div className="grid grid-cols-3 gap-3">
                  {DATA_SCOPE_OPTIONS.map(option => (
                    <label
                      key={option.value}
                      className={`flex flex-col p-3 rounded-lg cursor-pointer transition-colors border ${
                        newRolePermissions.dataScope === option.value
                          ? 'bg-purple-50 border-purple-300'
                          : 'bg-white border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="newRoleDataScope"
                          value={option.value}
                          checked={newRolePermissions.dataScope === option.value}
                          onChange={() => setNewRolePermissions({ ...newRolePermissions, dataScope: option.value })}
                          className="w-4 h-4 text-purple-600"
                        />
                        <span className={`text-sm font-medium ${
                          newRolePermissions.dataScope === option.value ? 'text-purple-700' : 'text-slate-700'
                        }`}>
                          {option.label}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1 ml-6">{option.description}</p>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* 功能权限 */}
            <div>
              <h4 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
                <Shield size={16} className="text-orange-600" />
                功能操作权限
              </h4>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <div className="space-y-4">
                  {Object.entries(PERMISSION_POINTS).map(([moduleId, perms]) => {
                    const modulePages = {
                      demand: '采购需求',
                      users: '客商管理',
                      order: '订单管理',
                      delivery: '送货单管理',
                      stockin: '入库单管理',
                      settlement: '结算管理',
                      invoice: '发票管理',
                      payment: '支付管理',
                      base: '基地管理',
                      materials: '物料管理',
                      employees: '员工管理',
                    };
                    return (
                      <div key={moduleId} className="bg-white p-3 rounded-lg border border-slate-200">
                        <h5 className="text-sm font-medium text-slate-700 mb-2">{modulePages[moduleId] || moduleId}</h5>
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(perms).map(([permKey, permLabel]) => {
                            const isEnabled = isNewRoleFunctionPermissionEnabled(moduleId, permKey);
                            return (
                              <label
                                key={`new-${moduleId}-${permKey}`}
                                className={`flex items-center gap-1.5 px-2.5 py-1 rounded cursor-pointer transition-colors border text-xs ${
                                  isEnabled
                                    ? 'bg-orange-50 border-orange-200 text-orange-700'
                                    : 'bg-slate-50 border-slate-200 text-slate-500 hover:bg-slate-100'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isEnabled}
                                  onChange={() => toggleNewRoleFunctionPermission(moduleId, permKey)}
                                  className="w-3 h-3 text-orange-600 rounded"
                                />
                                <span>{permLabel}</span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          <div className="p-4 border-t border-slate-100 bg-white flex justify-end gap-3">
            <button
              onClick={() => setShowAddModal(false)}
              className="px-4 py-2 text-slate-600 text-sm font-medium hover:bg-slate-50 rounded-lg"
            >
              取消
            </button>
            <button
              onClick={handleAddRole}
              className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 flex items-center gap-2"
            >
              <Plus size={16} /> 创建岗位
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-slate-800">岗位权限管理</h2>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          <Plus size={20} /> 新建岗位
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-100">
        <div className="p-4 border-b border-slate-100">
          <p className="text-sm text-slate-500">
            配置各岗位的页面访问权限、数据权限范围和功能操作权限。点击"配置"按钮修改岗位权限，点击"新建岗位"添加新的角色。
          </p>
        </div>
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 text-slate-500 border-b border-slate-100">
            <tr>
              <th className="p-4">岗位名称</th>
              <th className="p-4">岗位描述</th>
              <th className="p-4">页面权限</th>
              <th className="p-4">数据权限</th>
              <th className="p-4">功能权限</th>
              <th className="p-4">操作</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {roles.map(role => {
              const perm = getRolePermission(role.id);
              const pageCount = perm.pages.length;
              const dataScopeLabel = DATA_SCOPE_OPTIONS.find(o => o.value === perm.dataScope)?.label || '所属基地';
              const funcCount = Object.values(perm.permissions || {}).flat().length;

              return (
                <tr key={role.id} className="hover:bg-slate-50">
                  <td className="p-4 font-medium text-slate-800">
                    {role.label}
                    {role.id === 'admin' && (
                      <span className="ml-2 px-1.5 py-0.5 bg-amber-100 text-amber-600 text-[10px] rounded">系统</span>
                    )}
                  </td>
                  <td className="p-4 text-slate-500 text-xs">{role.description}</td>
                  <td className="p-4">
                    <span className="px-2 py-1 bg-blue-50 text-blue-600 rounded text-xs font-medium">
                      {pageCount} 个页面
                    </span>
                  </td>
                  <td className="p-4">
                    <span className="px-2 py-1 bg-purple-50 text-purple-600 rounded text-xs font-medium">
                      {dataScopeLabel}
                    </span>
                  </td>
                  <td className="p-4">
                    <span className="px-2 py-1 bg-orange-50 text-orange-600 rounded text-xs font-medium">
                      {funcCount} 项权限
                    </span>
                  </td>
                  <td className="p-4">
                    <div className="flex gap-3">
                      <button
                        className="text-blue-600 hover:underline text-xs font-medium"
                        onClick={() => handleEditRole(role)}
                      >
                        配置
                      </button>
                      {role.id !== 'admin' && (
                        <button
                          className="text-red-500 hover:underline text-xs font-medium"
                          onClick={() => handleDeleteRole(role)}
                        >
                          删除
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showEditModal && <EditPermissionModal />}
      {showAddModal && <AddRoleModal />}
    </div>
  );
};

export default RolePermissionView;
